import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter
} from "client/Navigation";
import { FunctionComponent, useEffect } from "react";
import { AddressBookContactSource } from "./Overview/Overview";
import { safeLocalStorage } from "client_legacy/ndaw/safeStorage";
import { ExchangeAvailability, useExchangeAvailability } from "client/Hooks";
import { SourceOption, useAvailableSourceOptions } from "./SourceSelector";
import { addressBookRouting, convertToSourcePath } from "./routing";
import { isNotNil } from "client/Misc/utils";
import { setHttpError } from "../ErrorPage/errorStore";

export type AddressBookSelectSourceProps = {
	onNavigate: NavigationCallback;
};

export const AddressBookSelectSource = (props: AddressBookSelectSourceProps): null => {
	const {
		onNavigate
	} = props;

	const exchangeAvailability: ExchangeAvailability = useExchangeAvailability();
	const availableSourceOptions: readonly SourceOption[] = useAvailableSourceOptions(exchangeAvailability);

	useEffect(
		(): void => {
			const previouslyPersistedSource: AddressBookContactSource | nil = safeLocalStorage
				.getItem<AddressBookContactSource>(LOCAL_STORAGE_KEY);

			const sourcesBasedOnPriority: AddressBookContactSource[] = [];

			if (typeof previouslyPersistedSource === "number"
				&& availableSourceOptions.some(x => x.source === previouslyPersistedSource)
			) {
				sourcesBasedOnPriority.push(previouslyPersistedSource);
			}

			const firstAvailable: AddressBookContactSource | undefined = availableSourceOptions[0]
				?.source;

			if (firstAvailable) {
				sourcesBasedOnPriority.push(firstAvailable);
			}

			for (const source of sourcesBasedOnPriority) {
				const sourcePath: string | undefined = convertToSourcePath(source);

				if (isNotNil(sourcePath)) {
					onNavigate(
						addressBookRouting.overview({
							source: sourcePath
						}),
						{
							shouldReplace: true
						}
					);

					return;
				}
			}

			setHttpError({
				statusCode: 404
			});
		},
		[availableSourceOptions, onNavigate]
	);

	return null;
};
AddressBookSelectSource.displayName = "AddressBookSelectSource";

export const AddressBookSelectSourceForRouter: FunctionComponent = connectToRouter(
	AddressBookSelectSource,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export function persistLastSelectedSource(
	source: AddressBookContactSource
): void {
	safeLocalStorage.setItem(LOCAL_STORAGE_KEY, source);
}

const LOCAL_STORAGE_KEY = "AddressBook_DefaultContactSource";